import React, { useCallback, useEffect, useRef, useState } from "react";
import FoodItem from "./FoodItem";
import Skeleton from "./Skeleton";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts, clearProducts } from '../../redux/menu/ProductSlice';
import { useNavigate } from 'react-router-dom';
import { BsCart2 } from 'react-icons/bs';

// Add mobile-specific styles
const mobileStyles = `
  @media (max-width: 640px) {
    .category-division {
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding-left: 16px !important;
      padding-right: 16px !important;
      will-change: transform;
      transform: translateZ(0);
      backface-visibility: hidden;
      perspective: 1000px;
    }
    .category-division::-webkit-scrollbar {
      display: none;
    }
    .sticky-categories {
      top: 64px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      z-index: 50 !important;
    }
  }
  @media (min-width: 641px) and (max-width: 768px) {
    .sticky-categories {
      top: 64px !important;
    }
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    .sticky-categories {
      top: 88px !important;
    }
  }
  @media (min-width: 1025px) {
    .sticky-categories {
      top: 120px !important;
    }
  }
`;

const Foods = ({ showCartButton = true, showAddToCart = true, highlightInfoIcon = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const menuReduxValue = useSelector(state => state.menu);
  const { categories, status: categoriesStatus } = useSelector(state => state.category);
  const { products, status: productsStatus, isSearchMode, isOffersMode } = useSelector(state => state.product);
  const { profile } = useSelector(state => state.profile);
  const cartItems = useSelector(state => state.cart.cart);


  // Refs for drag scroll
  const categoryBarRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // Sticky
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef(null);

  // Auto-scroll
  const isAutoScrolling = true;
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollRef = useRef(null);
  const lastScrollPosition = useRef(0);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoScrolling || isPaused || !categoryBarRef.current) return;

    const scrollContainer = categoryBarRef.current;
    const scrollContent = scrollContainer.querySelector('.category-division');
    
    if (!scrollContent) return;

    const scrollWidth = scrollContent.scrollWidth;
    const containerWidth = scrollContainer.clientWidth;
    
    if (scrollWidth <= containerWidth) return; // No need to scroll if content fits

    let scrollPosition = lastScrollPosition.current;
    const scrollSpeed =.3; // Increased scroll speed
    const maxScroll = scrollWidth - containerWidth;

    const autoScroll = () => {
      if (isPaused || isDragging.current) return;
      
      scrollPosition += scrollSpeed;
      
      if (scrollPosition >= maxScroll) {
        scrollPosition = 0; // Reset to beginning
      }
      
      // Use requestAnimationFrame for smoother animation
      requestAnimationFrame(() => {
        if (scrollContainer && !isPaused && !isDragging.current) {
          scrollContainer.scrollLeft = scrollPosition;
          lastScrollPosition.current = scrollPosition;
        }
      });
    };

    autoScrollRef.current = setInterval(autoScroll, 16); // ~60fps for smoother animation

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isAutoScrolling, isPaused, categories]);

  // Drag-to-scroll
  const handleMouseDown = (e) => {
    isDragging.current = true;
    setIsPaused(true);
    startX.current = e.pageX - categoryBarRef.current.offsetLeft;
    scrollLeft.current = categoryBarRef.current.scrollLeft;
  };
  const handleMouseLeave = () => { 
    isDragging.current = false; 
    setIsPaused(false);
    // Save the current scroll position when mouse leaves
    if (categoryBarRef.current) {
      lastScrollPosition.current = categoryBarRef.current.scrollLeft;
    }
  };
  const handleMouseUp = () => { 
    isDragging.current = false; 
    setIsPaused(false);
    // Save the current scroll position when user stops dragging
    if (categoryBarRef.current) {
      lastScrollPosition.current = categoryBarRef.current.scrollLeft;
    }
  };
  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - categoryBarRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    categoryBarRef.current.scrollLeft = scrollLeft.current - walk;
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    isDragging.current = true;
    setIsPaused(true);
    startX.current = e.touches[0].pageX - categoryBarRef.current.offsetLeft;
    scrollLeft.current = categoryBarRef.current.scrollLeft;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.touches[0].pageX - categoryBarRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    categoryBarRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    setIsPaused(false);
    // Save the current scroll position when user stops touching
    if (categoryBarRef.current) {
      lastScrollPosition.current = categoryBarRef.current.scrollLeft;
    }
  };

  // Add touch event listeners with passive: false to allow preventDefault
  useEffect(() => {
    const categoryBar = categoryBarRef.current;
    if (!categoryBar) return;

    const touchStartHandler = (e) => handleTouchStart(e);
    const touchMoveHandler = (e) => handleTouchMove(e);
    const touchEndHandler = () => handleTouchEnd();

    // Add event listeners with passive: false
    categoryBar.addEventListener('touchstart', touchStartHandler, { passive: false });
    categoryBar.addEventListener('touchmove', touchMoveHandler, { passive: false });
    categoryBar.addEventListener('touchend', touchEndHandler, { passive: false });

    return () => {
      categoryBar.removeEventListener('touchstart', touchStartHandler);
      categoryBar.removeEventListener('touchmove', touchMoveHandler);
      categoryBar.removeEventListener('touchend', touchEndHandler);
    };
  }, []);

  const [menuTab, setMenuTab] = useState(menuReduxValue?.selectedCategory);
  const [currentCategoryId, setCurrentCategoryId] = useState('');

  const loadProducts = useCallback((categoryId, page = 1) => {
    if (categoryId) {
      dispatch(fetchProducts({ categoryID: categoryId, page }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (categories && categories.length > 0) {
      if (!menuTab && categories[0]) {
        setMenuTab(categories[0].name);
        setCurrentCategoryId(categories[0].id);
        loadProducts(categories[0].id, 1);
      }
    }
  }, [categories, menuTab, dispatch, loadProducts]);

  useEffect(() => {
    if (menuTab && categories) {
      const selectedCategory = categories.find(cat => cat.name === menuTab);
      if (selectedCategory && selectedCategory.id !== currentCategoryId) {
        setCurrentCategoryId(selectedCategory.id);
        loadProducts(selectedCategory.id, 1);
      }
    }
  }, [menuTab, categories, currentCategoryId, loadProducts]);



  // Cart persistence is handled by localStorage, no need to reset on mount

  const handleMenuTabs = (type, index) => {
    setMenuTab(type);
    dispatch(clearProducts());
  };

  // Sticky handler like plain JS
  useEffect(() => {
    const handleScroll = () => {
      if (!stickyRef.current) return;
      const stickyTop = stickyRef.current.offsetTop;
      setIsSticky(window.pageYOffset > stickyTop);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate total cart items
  const totalCartItems = cartItems ? cartItems.reduce((total, item) => total + (item.quantity || 0), 0) : 0;

  // Cart button refs and handlers
  const iconRef = useRef(null);
  const handleCartMouseEnter = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = 'scale(1.1) rotate(5deg)';
    }
  };
  const handleCartMouseLeave = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = 'scale(1) rotate(0deg)';
    }
  };

  return (
    <>
      <style>{mobileStyles}</style>
      <section className={`${isSticky ? 'pt-4 md:pt-6' : ''} md:my-12 max-w-screen-xl mx-auto px-4 md:px-6 pb-8 md:pb-0`}>
        {/* Sticky Header - Only show when not in search mode */}
        {!isSearchMode && (
          <div
            ref={stickyRef}
            className={`${isSticky ? "fixed left-0 right-0 z-50 bg-white shadow-md sticky-categories" : ""}`}
          >
          <div
            ref={categoryBarRef}
            className="border-b border-gray-200 bg-white"
            style={{
              WebkitOverflowScrolling: 'touch',
              cursor: isDragging.current ? 'grabbing' : 'grab',
              width: '100%',
              overflow: 'hidden'
            }}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsPaused(true)}
          >
            <div 
              className="flex items-center space-x-2 md:space-x-6 category-division overflow-x-auto whitespace-nowrap py-3 md:py-4 px-4 md:px-6 scrollbar-hide"
              style={{ 
                minWidth: 'max-content',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                touchAction: 'pan-x',
                overflowX: 'auto',
                width: 'max-content',
                paddingLeft: '16px',
                paddingRight: '16px',
                willChange: 'transform',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                perspective: '1000px'
              }}
            >
              {categoriesStatus === 'loading' ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="animate-pulse flex-shrink-0">
                    <div className="w-20 md:w-24 h-8 md:h-10 bg-gray-200 rounded-full"></div>
                  </div>
                ))
              ) : (
                categories && categories.map((categoryItem, index) => (
                  <div
                    key={categoryItem.id || index}
                    className={`flex-shrink-0 ${
                      menuTab === categoryItem.name
                        ? "active_menu_tab poppins bg-primary/10 text-primary px-2 md:px-3 py-1 md:py-2 font-medium text-xs transition flex flex-col items-center rounded-lg"
                        : "menu_tab poppins text-gray-600 px-2 md:px-3 py-1 md:py-2 font-medium text-xs hover:text-primary transition flex flex-col items-center"
                    }`}
                    onClick={() => handleMenuTabs(categoryItem.name, index)}
                  >
                    {categoryItem.DocImage?.imageUrl ? (
                      <img 
                        src={categoryItem.DocImage.imageUrl} 
                        alt={categoryItem.name}
                        className={`w-8 h-8 md:w-12 md:h-12 object-contain rounded-full mb-1 md:mb-2 border border-gray-300 p-1`}
                        style={{backgroundColor :menuTab === categoryItem.name ? '#f91944' : ''}}
                      />
                    ) : (
                      <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full mb-1 md:mb-2 border border-gray-300 p-1 flex items-center justify-center ${
                        menuTab === categoryItem.name ? 'bg-primary/10' : ''
                      }`}>
                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16" className="md:w-8 md:h-8">
                          <circle cx="8" cy="8" r="7" stroke="#f91944" strokeWidth="2" fill="#fff"/>
                          <text x="8" y="12" textAnchor="middle" fontSize="10" fill="#f91944">🍽️</text>
                        </svg>
                      </div>
                    )}
                    <span className={`px-2 md:px-4 py-1 md:py-2 rounded-lg text-xs md:text-sm ${menuTab === categoryItem.name ? 'bg-primary text-white' : ''}`}>
                      {categoryItem.name}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offers Results Header - Only show when offers mode is active */}
      {isOffersMode && productsStatus === 'succeeded' && products && products.length > 0 && (
        <div className="text-center mt-4 mb-2">
          <div className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500/10 to-orange-600/5 px-6 py-3 rounded-full border border-orange-500/20">
            <span className="text-orange-600 font-semibold text-lg">
              🏷️ {products.length} Amazing Offer{products.length !== 1 ? 's' : ''} Available!
            </span>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 lg:gap-8 ${!isSearchMode ? 'mt-8' : 'mt-0'} h-[420px]`}>
        {productsStatus === 'loading' && products.length === 0 ? (
          Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} />)
        ) : products.length === 0 ? (
          <div className="col-span-4 flex items-center justify-center py-12 h-[420px]">
            <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-2xl px-8 py-10 flex flex-col items-center max-w-xs w-full border border-gray-100">
              <svg className="w-20 h-20 mb-6 text-primary/80" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" fill="#f3f4f6"/><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5"/><path d="M9.5 10.5c.5-1 2.5-1 3 0" stroke="#f91944" strokeWidth="1.2" strokeLinecap="round"/><circle cx="9" cy="9" r="1" fill="#f91944"/><circle cx="13" cy="9" r="1" fill="#f91944"/></svg>
              <h2 className="text-2xl font-extrabold text-primary mb-2 poppins text-center">No items found</h2>
              <p className="text-gray-500 text-base text-center mt-2">We couldn't find any menu items in this category.<br/>Try another category or check back later!</p>
            </div>
          </div>
        ) : (
          (() => {
            // Process products to swap highlighted items in even positions
            const processedProducts = [...products];
            
            for (let i = 0; i < processedProducts.length; i++) {
              const currentItem = processedProducts[i];
              const position = i + 1; // index + 1 for position
              
              // Check if current item is highlighted and in even position
              if (currentItem?.highlight && position % 2 === 0 && i > 0) {
                const previousItem = processedProducts[i - 1];
                
                // Check if previous item is not highlighted
                if (!previousItem?.highlight) {
                  // Swap positions
                  processedProducts[i] = previousItem;
                  processedProducts[i - 1] = currentItem;
                }
              }
            }
            
            return processedProducts.map((item) => {
              const currentCategory = categories?.find(cat => cat.name === menuTab);
              return (
                <FoodItem 
                  key={item.id} 
                  {...item} 
                  currency={profile?.currency || 'AED'} 
                  image={item?.DocImage?.imageUrl} 
                  is_veg={item?.is_veg}
                  categoryName={currentCategory?.name}
                  categoryDescription={currentCategory?.description}
                  showAddToCart={showAddToCart}
                  highlightInfoIcon={highlightInfoIcon}
                  highlight={item?.highlight || false}
                />
              );
            });
          })()
        )}
      </div>

      {/* Cart Button with Count - Hidden on Mobile */}
      {showCartButton && (
        <div className="fixed z-50 bottom-6 right-6 hidden md:block">
          <button
            className="bg-gradient-to-br from-red-500 via-pink-500 to-yellow-400 text-white rounded-full shadow-2xl w-20 h-20 flex items-center justify-center text-4xl border-2 border-white border-opacity-90 transition-transform duration-300 hover:scale-110 hover:shadow-[0_0_40px_12px_rgba(236,72,153,0.25)] relative"
            style={{ boxShadow: '0 4px 32px 0 rgba(249,25,68,0.18)', borderWidth: '2.5px' }}
            aria-label="View Cart"
            onClick={() => navigate(`/${profile?.key}/orders`)}
            onMouseEnter={handleCartMouseEnter}
            onMouseLeave={handleCartMouseLeave}
          >
            <span ref={iconRef} className="inline-block text-5xl font-bold">
              <BsCart2 />
            </span>
            {totalCartItems > 0 && (
              <div className="absolute -top-1 -right-1 bg-yellow-400 text-gray-800 text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center z-10 border-2 border-white shadow-lg">
                {totalCartItems}
              </div>
            )}
          </button>
        </div>
      )}
        </section>
      </>
    );
};

export default Foods;